import type {
  DerivedStats,
  DieResult,
  DieSpellId,
  MasterDie,
  SlotSpellId,
  TriggeredSpell,
} from './types';
import { BALANCE } from '../data/balance';
import { DIE_SPELLS, DIE_SPELL_BY_ID } from '../data/dieSpells';
import { SLOT_SPELL_BY_ID, slotSpellsFor } from '../data/slotSpells';

/**
 * Détection des sorts d'un lancer — **COMMENTS v0.4.1 §1.4**, arbitrages
 * A3 / A4 / A6 / A9 (DECISIONS §E).
 *
 * Deux familles, évaluées sur les faces des dés **Maîtres** uniquement (la face
 * d'un dé absorbé par un bouclier compte quand même — D.8) :
 *
 *  - **sorts d'emplacement** (`src/data/slotSpells.ts`) : débloqués par le
 *    nombre d'emplacements (`DerivedStats.slots`), **un seul** par lancer.
 *    Priorité (A6 / **A16**) : *Yeux du serpent* quand toutes les faces sont
 *    minimales, puis le multiplicateur de dégâts le plus fort — *Suite* et
 *    *Full*, dont le multiplicateur se calcule sur les faces, sont comparés
 *    avec leur valeur réelle ;
 *  - **sorts de dé** (`src/data/dieSpells.ts`) : portés par les dés Maîtres
 *    équipés (`MasterDie.spells`). **Tous** ceux qui correspondent se
 *    déclenchent (A9) ; un même sort porté par deux dés ne compte qu'une fois.
 *
 * Le facteur global — *Puissance des sorts* (`1 + 0,1 × niveau`) — s'applique
 * **une seule fois** au lancer :
 *
 *   `D_S = Π(multiplicateurs des sorts) × spellPower`
 *
 * Fonctions **pures** : aucun `Rng`, aucun effet de bord. La loterie de *666*
 * est résolue à l'application du lancer (`core/game.ts`).
 *
 * v0.47.0 (A94-10, 11) : les cinq sorts de *Poker* (nœud `aby_poker`) et les
 * deux **Grimoires** — renfort `m′ = 1 + (m − 1) × (1 + 0,1 × niveau)` des
 * sorts de dé (*Grimoire des nombres*) et d'emplacement (*Face je gagne, Pile
 * tu perds*), conditions élargies au niveau 3, Carré dès trois dés au niveau 5.
 *
 * v0.50.0 (COMMENTS v0.49.1 §1.1 et §1.3, A100-1, A100-2) : *Préparation*
 * n'est plus un compteur mais un renfort des sorts de dé portés par les dés des
 * emplacements 1 à 3, de la forme d'*Arrière-garde* ; le Mage Maudit divise
 * par 2 la part de bonus de chaque sort. Ordre de composition d'un sort :
 * **Arrière-garde → Préparation → Grimoire → Mage Maudit**.
 */

/** Une face tirée, avec le nombre de faces du dé (pour *Maxroll*). */
export interface RolledFace {
  face: number;
  sides: number;
}

export interface SpellContext {
  /** Emplacements de dés Maîtres débloqués (1 à 5). */
  slots: number;
  /** Sorts portés par les dés Maîtres du lancer (doublons tolérés). */
  dieSpells: readonly DieSpellId[];
  /** *Puissance des sorts* : `1 + 0,1 × niveau`. */
  spellPower: number;
  /**
   * *Préparation* (v0.50.0, A100-2) : sorts de dé portés par les dés Maîtres
   * des **emplacements 1 à 3** (`spells`) et renfort `boost = 0,1 × niveau`
   * (`DerivedStats.prepBoost`). Chaque sort de dé dont l'un de ces dés est
   * porteur, **après** *Arrière-garde* : `m′ = 1 + (m − 1) × (1 + boost)` si
   * `m > 1`, secondes et or (*Nombre d'or*) × `(1 + boost)`. Absent : aucun.
   */
  prep?: { spells: readonly DieSpellId[]; boost: number } | null;
  /**
   * Mage Maudit (v0.50.0, A100-1) : diviseur de la **part de bonus** de chaque
   * sort, appliqué **en dernier** (après les Grimoires) — `m′ = 1 + (m − 1) ÷
   * divisor` sur les multiplicateurs de dégâts (*Yeux du serpent* adouci) et
   * sur l'or de *Nombre d'or* ; les secondes sont intactes. 1 ou absent : aucun.
   */
  halveSpells?: number;
  /**
   * *ArchiDicer* (A72-16) : sort de dé **forcé**, ajouté comme s'il était sorti
   * (marqué `forced: true`) s'il ne s'est pas déjà déclenché.
   */
  forced?: DieSpellId | null;
  /**
   * *Arrière-garde* (A89-6) : sorts de dé portés par les dés des deux derniers
   * emplacements équipés (`spells`) et renfort `boost = 0,04 × niveau`. Chaque
   * sort de dé de dégâts déclenché (`m > 1`) dont l'un de ces dés est porteur
   * passe à `m' = 1 + (1 + boost) × (m − 1)`.
   */
  rearguard?: { spells: readonly DieSpellId[]; boost: number } | null;
  /** Sorts de *Poker* débloqués (`aby_poker`, A94-10) ? Faux par défaut. */
  poker?: boolean;
  /**
   * *Grimoire des nombres* (0 à 5, A94-11) : renfort des sorts de dé (après
   * *Arrière-garde*), secondes de *42* et or de *Nombre d'or* × (1 + 0,1 ×
   * niveau) ; conditions élargies au niveau 3. 0 par défaut.
   */
  grimoireNumbers?: number;
  /**
   * *Face je gagne, Pile tu perds* (0 à 5) : renfort du sort d'emplacement,
   * *Yeux du serpent* adouci ; Suites à trou au niveau 3, Carré dès trois dés
   * au niveau 5. 0 par défaut.
   */
  grimoireFaces?: number;
  /**
   * *Rouleau Compresseur* (v0.49.0, A98-4) : plus haute face Maître du lancer
   * précédent appliqué du combat (`state.combat.lastMaxFace`) ; 0 ou absent au
   * premier lancer, où le sort ne se déclenche jamais.
   */
  lastMaxFace?: number;
}

/** Résultat complet de la détection des sorts d'un lancer. */
export interface SpellOutcome {
  /** Tous les sorts déclenchés, valeurs **nues**. */
  spells: TriggeredSpell[];
  /** `D_S` : produit des multiplicateurs (renforts et Mage Maudit compris) × `spellPower`. */
  multiplier: number;
  /** Secondes accordées au chrono, facteurs globaux inclus. */
  seconds: number;
  /** Multiplicateur d'or de l'ennemi tué, facteurs globaux inclus. */
  goldMult: number;
  /**
   * `[spellPower]` si un sort se déclenche, vide sinon — v0.50.0 (A100-2) : le
   * second élément (`1 + compteur de Préparation`) a disparu avec le compteur.
   */
  effectMultipliers: number[];
  /** Sort le plus fort en dégâts (à défaut le premier) — résumé de compatibilité. */
  summary: TriggeredSpell | null;
  /** Facteur obtenu par le *Grimoire des nombres* sur les sorts de dé (1 sans effet). */
  grimoireNumbersMult: number;
  /** Facteur obtenu par *Face je gagne* sur le sort d'emplacement (1 sans effet). */
  grimoireFacesMult: number;
  /**
   * *Préparation* (v0.50.0, A100-2) : facteur **global** obtenu sur les dégâts,
   * `Π m′ / m` des sorts de dé renforcés (1 sans effet).
   */
  prepMult: number;
  /** Mage Maudit (A100-1) : la part de bonus des sorts a-t-elle été divisée ? */
  halved: boolean;
}

/**
 * Facteur global appliqué aux effets de sorts : *Puissance des sorts*.
 * v0.50.0 (A100-2) : *Préparation* n'y entre plus (renfort par sort).
 */
export function spellEffectFactor(spellPower: number): number {
  return spellPower;
}

/** `n` est-il premier ? (petits entiers : nombre de dés Maîtres, faces jusqu'à 100) */
export function isPrime(n: number): boolean {
  if (!Number.isInteger(n) || n < 2) return false;
  for (let d = 2; d * d <= n; d++) if (n % d === 0) return false;
  return true;
}

/**
 * Face **première** pour *Premiers* — A89-2 : un nombre premier jusqu'à
 * `BALANCE.spellPrimeMaxFace` (97), lu **brut** sur la face (le d100 n'a
 * aucune lecture chiffre par chiffre).
 */
export function isPrimeFace(face: number): boolean {
  return face <= BALANCE.spellPrimeMaxFace && isPrime(face);
}

/**
 * *Arrière-garde* (A89-6) : multiplicateur renforcé d'un sort de dé de dégâts,
 * `m' = 1 + (1 + boost) × (m − 1)` avec `boost = 0,04 × niveau`. Un sort sans
 * dégâts (`m ≤ 1`) ou un renfort nul rend `m` tel quel.
 */
export function rearguardMultiplier(m: number, boost: number): number {
  if (!(m > 1) || !(boost > 0)) return m;
  return 1 + (1 + boost) * (m - 1);
}

/**
 * *Préparation* (v0.50.0, A100-2) : même forme qu'*Arrière-garde*, `m′ = 1 +
 * (m − 1) × (1 + boost)` avec `boost = 0,1 × niveau` ; `m ≤ 1` ou renfort nul :
 * `m` tel quel.
 */
export function prepMultiplier(m: number, boost: number): number {
  return rearguardMultiplier(m, boost);
}

/**
 * Mage Maudit (v0.50.0, A100-1) : la **part de bonus** d'un multiplicateur est
 * divisée, `m′ = 1 + (m − 1) ÷ divisor` — ×1,25 → ×1,125, ×1,5 → ×1,25, ×0,75
 * (*Yeux du serpent*) → ×0,875. Diviseur ≤ 1 ou m = 1 : m tel quel.
 */
export function halveSpellBonus(m: number, divisor: number | undefined): number {
  const d = divisor ?? 1;
  if (!(d > 1) || m === 1) return m;
  return 1 + (m - 1) / d;
}

/** Facteur d'un Grimoire : `1 + 0,1 × niveau` (A94-11). */
export function grimoireFactor(level: number | undefined): number {
  const l = Math.max(0, level ?? 0);
  return 1 + BALANCE.grimoirePerLevel * l;
}

/**
 * Multiplicateur renforcé par un Grimoire (A94-11) : `m′ = 1 + (m − 1) × (1 +
 * 0,1 × niveau)` pour m > 1 ; *Yeux du serpent* (m < 1) est **adouci** :
 * `m′ = 1 − (1 − m) × (1 − 0,1 × niveau)` (×0,875 au niveau 5). Niveau nul ou
 * m = 1 : m tel quel.
 */
export function grimoireMultiplier(m: number, level: number | undefined): number {
  const l = Math.max(0, level ?? 0);
  if (!(l > 0) || m === 1) return m;
  if (m > 1) return 1 + (m - 1) * grimoireFactor(l);
  return 1 - (1 - m) * Math.max(0, 1 - BALANCE.grimoirePerLevel * l);
}

/** Conditions élargies du *Grimoire des nombres* (niveau 3 et plus, A94-11) ? */
export function numbersWidened(level: number | undefined): boolean {
  return (level ?? 0) >= BALANCE.grimoireWidenLevel;
}

/** Suites à trou de *Face je gagne* (niveau 3 et plus) ? */
export function straightGapAllowed(level: number | undefined): boolean {
  return (level ?? 0) >= BALANCE.grimoireWidenLevel;
}

/** Carré dès trois dés identiques (*Face je gagne* niveau 5) ? */
export function fourFromThree(level: number | undefined): boolean {
  return (level ?? 0) >= BALANCE.grimoireFourLevel;
}

function counts(faces: readonly number[]): Map<number, number> {
  const out = new Map<number, number>();
  for (const f of faces) out.set(f, (out.get(f) ?? 0) + 1);
  return out;
}

// ────────────────────────────────────────────────── Sorts d'emplacement (A6)

/** Faces triées par ordre croissant, distinctes. */
function distinctSorted(faces: readonly number[]): number[] {
  return Array.from(new Set(faces)).sort((a, b) => a - b);
}

/**
 * Plus haute face d'une **suite** de `length` faces consécutives parmi les
 * faces données (5 par défaut : *Suite*, COMMENTS v0.6.0 §1.2.1 ; 6 : *Grande
 * suite* ; 7 : *Maxi suite*), ou 0 s'il n'y en a pas.
 *
 * `gaps` (v0.47.0, *Face je gagne* niveau 3) : `length` faces **distinctes**
 * couvrant au plus `length + gaps` valeurs consécutives — un trou toléré avec
 * `gaps = 1`. La plus haute face de la fenêtre compte.
 */
export function straightHigh(faces: readonly number[], length = 5, gaps = 0): number {
  const sorted = distinctSorted(faces);
  const n = Math.max(2, Math.floor(length));
  let best = 0;
  for (let i = 0; i + n - 1 < sorted.length; i++) {
    if (sorted[i + n - 1] - sorted[i] <= n - 1 + Math.max(0, gaps)) {
      best = Math.max(best, sorted[i + n - 1]);
    }
  }
  return best;
}

/**
 * *Brelan carré* (A94-10) : produit `face du brelan × face du carré` (faces
 * différentes), ou 0. À plusieurs choix, le produit le plus fort est retenu.
 */
export function tripleFourProduct(faces: readonly number[]): number {
  const c = counts(faces);
  let best = 0;
  for (const [four, n4] of c) {
    if (n4 < 4) continue;
    for (const [triple, n3] of c) {
      if (triple === four || n3 < 3) continue;
      best = Math.max(best, triple * four);
    }
  }
  return best;
}

/**
 * Produit `face du brelan × face de la paire` d'un **full**, ou 0 sans full.
 * Le brelan et la paire portent des faces différentes ; à égalité de choix
 * (par exemple un carré plus une paire), le produit le plus fort est retenu.
 */
export function fullProduct(faces: readonly number[]): number {
  const c = counts(faces);
  let best = 0;
  for (const [triple, n3] of c) {
    if (n3 < 3) continue;
    for (const [pair, n2] of c) {
      if (pair === triple || n2 < 2) continue;
      best = Math.max(best, triple * pair);
    }
  }
  return best;
}

/**
 * Multiplicateur d'un sort d'emplacement **sur ces faces**, ou 0 si le sort ne
 * se déclenche pas. Les sorts `dynamic` (*Suite*, *Full*, *Grande* et *Maxi
 * suite*, *Brelan carré*) le calculent ; les autres reprennent la constante de
 * leur définition. Valeur **nue** : le renfort de *Face je gagne* s'applique
 * dans `detectSpells`.
 *
 * `grimoireFaces` (niveau de *Face je gagne*) : Suites à trou au niveau 3,
 * Carré dès trois dés au niveau 5 (le Brelan reste défini, le plus fort gagne).
 */
export function slotSpellMultiplier(
  id: SlotSpellId,
  faces: readonly number[],
  grimoireFaces = 0,
): number {
  const def = SLOT_SPELL_BY_ID[id];
  const fourAtThree = id === 'four' && fourFromThree(grimoireFaces);
  if (faces.length < (fourAtThree ? 3 : def.requiredSlots)) return 0;
  const c = counts(faces);
  const gaps = straightGapAllowed(grimoireFaces) ? 1 : 0;
  switch (id) {
    case 'snakeEyes':
      return faces.every((f) => f === 1) ? def.multiplier : 0;
    case 'pair':
      return Array.from(c.values()).some((n) => n >= 2) ? def.multiplier : 0;
    case 'triple':
      return Array.from(c.values()).some((n) => n >= 3) ? def.multiplier : 0;
    case 'four':
      return Array.from(c.values()).some((n) => n >= (fourAtThree ? 3 : 4)) ? def.multiplier : 0;
    case 'doublePair': {
      // Deux **couples de faces différentes** (A16).
      const pairs = Array.from(c.values()).filter((n) => n >= 2).length;
      return pairs >= 2 ? def.multiplier : 0;
    }
    case 'straight':
      return straightHigh(faces, 5, gaps);
    case 'full':
      return fullProduct(faces);
    // ── *Poker* (A94-10).
    case 'triplePair': {
      // Trois paires de faces **différentes** (un brelan compte comme paire).
      const pairs = Array.from(c.values()).filter((n) => n >= 2).length;
      return pairs >= 3 ? def.multiplier : 0;
    }
    case 'doubleTriple': {
      const triples = Array.from(c.values()).filter((n) => n >= 3).length;
      return triples >= 2 ? def.multiplier : 0;
    }
    case 'grandStraight': {
      const high = straightHigh(faces, 6, gaps);
      return high > 0 ? Math.pow(high, BALANCE.spellGrandStraightPower) : 0;
    }
    case 'maxiStraight': {
      const high = straightHigh(faces, 7, gaps);
      return high > 0 ? Math.pow(high, BALANCE.spellMaxiStraightPower) : 0;
    }
    case 'tripleFour':
      return tripleFourProduct(faces);
    default:
      return 0;
  }
}

/**
 * Sort d'emplacement du lancer, ou `null`.
 *
 * Un sort d'emplacement demande autant de **dés Maîtres lancés** que
 * d'emplacements requis : à deux emplacements dont un vide, aucune paire n'est
 * possible (DECISIONS §D.16).
 *
 * *Yeux du serpent* prime sur tout le reste quand toutes les faces sont
 * minimales (A16) ; sinon le sort retenu est celui dont le **multiplicateur de
 * dégâts effectif** est le plus fort.
 */
export function detectSlotSpell(
  faces: readonly number[],
  ctx: Pick<SpellContext, 'slots'> &
    Partial<Pick<SpellContext, 'poker' | 'grimoireFaces'>>,
): SlotSpellId | null {
  // *Poker* (A94-10) : les cinq sorts demandent le nœud en plus des emplacements.
  const unlocked = slotSpellsFor(ctx.slots, ctx.poker ? { aby_poker: 1 } : undefined);
  if (unlocked.length === 0 || faces.length < 2) return null;
  const level = ctx.grimoireFaces ?? 0;

  // *Yeux du serpent* : toutes les faces à leur minimum (1).
  if (unlocked.includes('snakeEyes') && slotSpellMultiplier('snakeEyes', faces) > 0) {
    return 'snakeEyes';
  }

  // Le renfort de *Face je gagne* est croissant en m : comparer les valeurs
  // nues revient à comparer les valeurs renforcées. À **égalité**, un sort de
  // *Poker* l'emporte sur un sort de campagne : le *Brelan carré* vaut
  // toujours exactement le *Full* des mêmes faces et ne sortirait jamais
  // sinon (A94-10). Entre sorts de campagne, le premier du catalogue reste.
  let best: SlotSpellId | null = null;
  let bestMult = 0;
  for (const id of unlocked) {
    if (id === 'snakeEyes') continue;
    const mult = slotSpellMultiplier(id, faces, level);
    const pokerTie =
      mult > 0 &&
      mult === bestMult &&
      best !== null &&
      !!SLOT_SPELL_BY_ID[id].requiresNode &&
      !SLOT_SPELL_BY_ID[best].requiresNode;
    if (mult > bestMult || pokerTie) {
      bestMult = mult;
      best = id;
    }
  }
  return best;
}

// ───────────────────────────────────────────────────────── Sorts de dé (A9)

/** *Maxroll* : ×1,75, doublé par emplacement au-delà de 2 (A4). */
export function maxrollMultiplier(slots: number): number {
  return (
    BALANCE.spellMaxrollMultiplier * Math.pow(BALANCE.spellMaxrollSlotFactor, slots - 2)
  );
}

/**
 * Le sort de dé `id` se déclenche-t-il sur ces faces ?
 *
 * *Parité* et *Maxroll* quantifient sur « tous les dés » : ils demandent au
 * moins **deux** dés Maîtres lancés, sans quoi ils se déclencheraient en
 * permanence (DECISIONS §D.16).
 *
 * `wide` (v0.47.0, *Grimoire des nombres* niveau 3, A94-11) : conditions
 * **élargies**, qui remplacent les étroites — *Pi* : 3 et 1 plus une face
 * paire quelconque ; *Parité* : une face de l'autre parité tolérée (≥ 3 dés) ;
 * *Maxroll* : une face à max − 1 tolérée (≥ 3 dés) ; *666* : deux 6 ;
 * *Premiers* : un seul dé premier suffit. *42* et *Nombre d'or* inchangés.
 *
 * v0.49.0 (A98-4, A98-8) — *Vingt-dicte* : au moins 4 faces > 6 (3 élargi) ;
 * *Null* : aucune face en double, au moins deux dés (une paire tolérée
 * élargi) ; *Tempête* : 3 faces > 10 (2 élargi) ; *Rouleau Compresseur* :
 * plus haute face ≥ `prev.lastMaxFace` (≥ précédent − 1 élargi), jamais sans
 * lancer précédent.
 */
export function dieSpellMatches(
  id: DieSpellId,
  rolls: readonly RolledFace[],
  wide = false,
  prev: { lastMaxFace?: number } = {},
): boolean {
  const faces = rolls.map((r) => r.face);
  const c = counts(faces);
  const has = (face: number, n = 1): boolean => (c.get(face) ?? 0) >= n;
  switch (id) {
    case 'fortyTwo':
      return has(4) && has(2);
    case 'parity': {
      if (rolls.length < 2) return false;
      const evens = faces.filter((f) => f % 2 === 0).length;
      const odds = faces.length - evens;
      if (wide && rolls.length >= 3) return evens <= 1 || odds <= 1;
      return evens === 0 || odds === 0;
    }
    case 'pi':
      if (wide) return has(3) && has(1) && faces.some((f) => f % 2 === 0);
      return has(3) && has(1) && has(4);
    case 'maxroll': {
      if (rolls.length < 2) return false;
      if (wide && rolls.length >= 3) {
        const off = rolls.filter((r) => r.face !== r.sides);
        return off.length <= 1 && off.every((r) => r.face === r.sides - 1);
      }
      return rolls.every((r) => r.face === r.sides);
    }
    case 'goldenRatio':
      // 1, 6, 1, 8 : deux 1, un 6 et un 8 — inatteignable tant qu'il n'y a pas
      // de d8 dans le jeu (A4), implémenté tel quel. Un d12 en donne l'accès.
      return has(1, 2) && has(6) && has(8);
    case 'sixes':
      // *666* — l'Enfer : trois 6 sur les dés Maîtres (COMMENTS v0.6.0 §1.2.2) ;
      // deux suffisent avec le Grimoire élargi.
      return has(6, wide ? 2 : 3);
    case 'primes': {
      // *Premiers* (A17) : un **nombre premier** de dés Maîtres affichant une
      // face première (jusqu'à 97 depuis A89-2). Les dés Basiques ne comptent pas.
      // Grimoire élargi : un seul dé premier suffit, le compte n'a plus à être premier.
      let n = 0;
      for (const f of faces) if (isPrimeFace(f)) n += 1;
      return wide ? n >= 1 : isPrime(n);
    }
    case 'twentyDict': {
      const above = faces.filter((f) => f > BALANCE.spellTwentyDictFace).length;
      return above >= (wide ? BALANCE.spellTwentyDictWideCount : BALANCE.spellTwentyDictCount);
    }
    case 'nullMark': {
      if (rolls.length < 2) return false;
      // Faces en trop : Σ (occurrences − 1). Une paire tolérée au Grimoire niveau 3.
      let extra = 0;
      for (const n of c.values()) extra += n - 1;
      return extra <= (wide ? BALANCE.spellNullWidePairs : 0);
    }
    case 'storm': {
      const above = faces.filter((f) => f > BALANCE.spellStormFace).length;
      return above >= (wide ? BALANCE.spellStormWideCount : BALANCE.spellStormCount);
    }
    case 'steamroller': {
      const last = prev.lastMaxFace ?? 0;
      if (!(last > 0) || faces.length === 0) return false;
      return Math.max(...faces) >= last - (wide ? BALANCE.spellSteamrollerWideSlack : 0);
    }
    default:
      return false;
  }
}

/** *Rouleau Compresseur* : plus petite × plus haute face Maître du lancer (0 sans face). */
export function steamrollerMultiplier(faces: readonly number[]): number {
  if (faces.length === 0) return 0;
  return Math.min(...faces) * Math.max(...faces);
}

/** Sorts de dé distincts déclenchés par ce lancer, dans l'ordre du catalogue. */
export function detectDieSpells(
  rolls: readonly RolledFace[],
  ctx: Pick<SpellContext, 'dieSpells' | 'slots'> &
    Partial<Pick<SpellContext, 'grimoireNumbers' | 'lastMaxFace'>>,
): DieSpellId[] {
  if (rolls.length === 0) return [];
  const carried = new Set(ctx.dieSpells);
  const wide = numbersWidened(ctx.grimoireNumbers);
  const prev = { lastMaxFace: ctx.lastMaxFace ?? 0 };
  return DIE_SPELLS.filter(
    (def) => carried.has(def.id) && dieSpellMatches(def.id, rolls, wide, prev),
  ).map((def) => def.id);
}

// ─────────────────────────────────────────────────────────── Détection totale

function slotSpellOf(id: SlotSpellId, faces: readonly number[], grimoireFaces = 0): TriggeredSpell {
  const def = SLOT_SPELL_BY_ID[id];
  return {
    id,
    kind: 'slot',
    // *Suite*, *Full* et les sorts dynamiques du *Poker* : le multiplicateur
    // dépend des faces obtenues (et du trou toléré par *Face je gagne*).
    multiplier: def.dynamic ? slotSpellMultiplier(id, faces, grimoireFaces) : def.multiplier,
    bonusSeconds: 0,
    goldMult: 1,
    label: def.name,
  };
}

function dieSpellOf(id: DieSpellId, slots: number, faces: readonly number[]): TriggeredSpell {
  const def = DIE_SPELL_BY_ID[id];
  return {
    id,
    kind: 'die',
    // *Rouleau Compresseur* (A98-4) : plus petite × plus haute face (`dynamic`).
    multiplier:
      id === 'maxroll'
        ? maxrollMultiplier(slots)
        : id === 'steamroller'
          ? Math.max(1, steamrollerMultiplier(faces))
          : def.multiplier,
    bonusSeconds: def.bonusSeconds,
    goldMult: def.goldMult,
    label: def.name,
  };
}

/** Le sort « résumé » d'un lancer : le plus fort en dégâts, à défaut le premier. */
function summaryOf(spells: readonly TriggeredSpell[]): TriggeredSpell | null {
  if (spells.length === 0) return null;
  let best = spells[0];
  for (const s of spells) if (s.multiplier > best.multiplier) best = s;
  return best;
}

/** Détecte tous les sorts d'un lancer et compose leurs effets. */
export function detectSpells(rolls: readonly RolledFace[], ctx: SpellContext): SpellOutcome {
  const faces = rolls.map((r) => r.face);
  const spells: TriggeredSpell[] = [];

  const slotSpell = detectSlotSpell(faces, ctx);
  if (slotSpell) spells.push(slotSpellOf(slotSpell, faces, ctx.grimoireFaces ?? 0));
  const dieIds = detectDieSpells(rolls, ctx);
  for (const id of dieIds) spells.push(dieSpellOf(id, ctx.slots, faces));
  if (ctx.forced && DIE_SPELL_BY_ID[ctx.forced] && !dieIds.includes(ctx.forced)) {
    spells.push({ ...dieSpellOf(ctx.forced, ctx.slots, faces), forced: true });
  }
  // *Arrière-garde* : un sort renforcé reste **un** sort (déclenché une fois).
  const rear = ctx.rearguard;
  if (rear && rear.boost > 0 && rear.spells.length > 0) {
    for (const sp of spells) {
      if (sp.kind !== 'die' || !(sp.multiplier > 1)) continue;
      if (!rear.spells.includes(sp.id as DieSpellId)) continue;
      const m = sp.multiplier;
      const mPrime = rearguardMultiplier(m, rear.boost);
      sp.multiplier = mPrime;
      sp.rearguard = true;
      sp.m = m;
      sp.mPrime = mPrime;
    }
  }

  /**
   * *Préparation* (v0.50.0, A100-2), **après** *Arrière-garde* : un sort de dé
   * porté par un dé des emplacements 1 à 3 voit sa part de bonus multipliée
   * par `1 + boost`, ses secondes et son or (*Nombre d'or*) × `(1 + boost)`.
   * Un dé à la fois parmi les trois premiers et les deux derniers cumule.
   */
  let prepMult = 1;
  const prep = ctx.prep;
  if (prep && prep.boost > 0 && prep.spells.length > 0) {
    for (const sp of spells) {
      if (sp.kind !== 'die' || !prep.spells.includes(sp.id as DieSpellId)) continue;
      const m = sp.multiplier;
      const mPrime = prepMultiplier(m, prep.boost);
      if (mPrime !== m) {
        sp.prepFrom = m;
        sp.multiplier = mPrime;
        prepMult *= mPrime / m;
      }
      if (sp.bonusSeconds > 0) sp.bonusSeconds *= 1 + prep.boost;
      if (sp.goldMult !== 1) sp.goldMult *= 1 + prep.boost;
    }
  }

  /**
   * Grimoires (A94-11), **après** *Arrière-garde* et *Préparation* : le sort d'emplacement par
   * *Face je gagne* (*Yeux du serpent* adouci), les sorts de dé de dégâts par
   * le *Grimoire des nombres* (*Rouleau Compresseur* compris, A98-8), qui
   * multiplie aussi les secondes d'un sort et l'or de *Nombre d'or* par
   * `1 + 0,1 × niveau`. *Vingt-dicte*, *Null* et *Tempête* (×1) n'y gagnent
   * rien ici ; la *Tempête* est renforcée dans `Game.stormFactor`.
   */
  let grimoireNumbersMult = 1;
  let grimoireFacesMult = 1;
  const numbers = ctx.grimoireNumbers ?? 0;
  const facesLevel = ctx.grimoireFaces ?? 0;
  for (const sp of spells) {
    const level = sp.kind === 'slot' ? facesLevel : numbers;
    if (!(level > 0)) continue;
    const m = sp.multiplier;
    const mPrime = grimoireMultiplier(m, level);
    if (mPrime !== m) {
      sp.grimoireFrom = m;
      sp.multiplier = mPrime;
      if (sp.kind === 'slot') grimoireFacesMult *= mPrime / m;
      else grimoireNumbersMult *= mPrime / m;
    }
    if (sp.kind === 'die') {
      if (sp.bonusSeconds > 0) sp.bonusSeconds *= grimoireFactor(level);
      if (sp.goldMult !== 1) sp.goldMult *= grimoireFactor(level);
    }
  }

  /**
   * Mage Maudit (v0.50.0, A100-1), **en dernier** : la part de bonus de chaque
   * multiplicateur de dégâts et de l'or de *Nombre d'or* est divisée. Les
   * secondes, *Vingt-dicte*, *Null* et *Tempête* (×1) ne bougent pas.
   */
  const divisor = ctx.halveSpells ?? 1;
  const halved = divisor > 1 && spells.length > 0;
  if (halved) {
    for (const sp of spells) {
      const m = sp.multiplier;
      const mPrime = halveSpellBonus(m, divisor);
      if (mPrime !== m) {
        sp.halvedFrom = m;
        sp.multiplier = mPrime;
      }
      if (sp.goldMult !== 1) sp.goldMult = halveSpellBonus(sp.goldMult, divisor);
    }
  }

  if (spells.length === 0) {
    return {
      spells,
      multiplier: 1,
      seconds: 0,
      goldMult: 1,
      effectMultipliers: [],
      summary: null,
      grimoireNumbersMult,
      grimoireFacesMult,
      prepMult,
      halved,
    };
  }

  const factor = spellEffectFactor(ctx.spellPower);
  let product = 1;
  let seconds = 0;
  let goldMult = 1;
  for (const s of spells) {
    product *= s.multiplier;
    seconds += s.bonusSeconds;
    goldMult *= s.goldMult;
  }
  return {
    spells,
    multiplier: product * factor,
    seconds: seconds * factor,
    goldMult: goldMult === 1 ? 1 : goldMult * factor,
    effectMultipliers: [ctx.spellPower],
    summary: summaryOf(spells),
    grimoireNumbersMult,
    grimoireFacesMult,
    prepMult,
    halved,
  };
}

/** Sorts de dé portés par les dés Maîtres équipés, dédoublonnés (A9). */
export function carriedDieSpells(masters: readonly MasterDie[]): DieSpellId[] {
  const out: DieSpellId[] = [];
  for (const die of masters) {
    for (const id of die.spells ?? []) {
      if (DIE_SPELL_BY_ID[id] && !out.includes(id)) out.push(id);
    }
  }
  return out;
}

/** Raccourci : détecte les sorts d'un lancer à partir de ses `DieResult`. */
export function detectSpellsFromDice(
  dice: readonly DieResult[],
  masters: readonly MasterDie[],
  stats: DerivedStats,
  opts: {
    forced?: DieSpellId | null;
    rearguard?: SpellContext['rearguard'];
    prep?: SpellContext['prep'];
    halveSpells?: number;
    lastMaxFace?: number;
  } = {},
): SpellOutcome {
  // v0.49.0 : la face d'un dé **annulé** par un boss (`voided`) ne compte pas
  // dans les sorts ; celle d'un dé absorbé par un bouclier, si (D.8).
  const rolls: RolledFace[] = dice
    .filter((d) => d.isMaster && !d.voided)
    .map((d) => ({ face: d.face, sides: d.sides }));
  return detectSpells(rolls, spellContextOf(stats, carriedDieSpells(masters), opts));
}

/** Les sorts de *Poker* sont-ils débloqués dans ces statistiques (A94-10) ? */
export function pokerUnlocked(stats: Pick<DerivedStats, 'unlockedSpells'>): boolean {
  return (stats.unlockedSpells ?? []).some((id) => SLOT_SPELL_BY_ID[id]?.requiresNode);
}

/** `SpellContext` d'un lancer à partir des statistiques dérivées (Poker et Grimoires compris). */
export function spellContextOf(
  stats: DerivedStats,
  dieSpells: readonly DieSpellId[],
  opts: {
    forced?: DieSpellId | null;
    rearguard?: SpellContext['rearguard'];
    prep?: SpellContext['prep'];
    halveSpells?: number;
    lastMaxFace?: number;
  } = {},
): SpellContext {
  return {
    lastMaxFace: opts.lastMaxFace ?? 0,
    slots: stats.slots,
    dieSpells,
    spellPower: stats.spellPower,
    forced: opts.forced ?? null,
    rearguard: opts.rearguard ?? null,
    prep: opts.prep ?? null,
    halveSpells: opts.halveSpells ?? 1,
    poker: pokerUnlocked(stats),
    grimoireNumbers: stats.grimoireNumbers ?? 0,
    grimoireFaces: stats.grimoireFaces ?? 0,
  };
}
